import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { forwardRef, Directive, Input, EventEmitter, Component, ChangeDetectionStrategy, ViewEncapsulation, ViewChild, Output, ContentChildren, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i1 from '@appkit4/angular-components/shared';

const CHECKBOX_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CheckboxComponent),
    multi: true,
};
class CustomizeTemplate {
    constructor(template) {
        this.template = template;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CustomizeTemplate, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.11", type: CustomizeTemplate, selector: "[ngTemplate]", inputs: { type: "type", name: ["ngTemplate", "name"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CustomizeTemplate, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ngTemplate]",
                    host: {},
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }], propDecorators: { type: [{
                type: Input
            }], name: [{
                type: Input,
                args: ["ngTemplate"]
            }] } });
class CheckboxComponent {
    constructor(cd, el, render, focusIndicatorService) {
        this.cd = cd;
        this.el = el;
        this.render = render;
        this.focusIndicatorService = focusIndicatorService;
        this.tabindex = 0;
        this.indeterminate = false;
        this.labelPosition = "after";
        this.stopKeydownPropagation = true;
        this.onChange = new EventEmitter();
        this.onIndeterminateChange = new EventEmitter();
        this.onModelChange = () => { };
        this.onModelTouched = () => { };
        this.focused = false;
        this.checked = false;
    }
    ngOnInit() {
        if (this.disabled) {
            this.tabindex = -1;
        }
        if (!this.inputId) {
            this.inputId =
                "checkbox" +
                    String.fromCharCode(65 + Math.floor(Math.random() * 26)) +
                    Date.now();
        }
        if (this.style && this.el.nativeElement.children?.[0])
            this.render.setStyle(this.el.nativeElement.children?.[0], 'cssText', this.style);
    }
    ngAfterContentInit() {
        if (this.templates.length) {
            this.templateMap = {};
        }
        this.templates.forEach((item) => {
            this.templateMap[item.name] = item.template;
        });
    }
    ngOnChanges(changes) {
        for (let propName in changes) {
            let change = changes[propName];
            if (propName === "disabled" && !changes[propName].firstChange) {
                this.disabled = change.currentValue;
                if (this.disabled) {
                    this.tabindex = -1;
                }
                else {
                    this.tabindex = 0;
                }
            }
            // Listen the input change of 'indeterminate' and emit its current value
            if (propName === "indeterminate" && !change.firstChange) {
                this.onIndeterminateChange.emit(change.currentValue);
            }
        }
    }
    onCheckedChange(event) {
        if (this.indeterminate) {
            this.checked = true;
            this.indeterminate = false;
            this.onIndeterminateChange.emit(this.indeterminate);
            this.onModelChange(this.checked);
            this.onChange.emit({
                originEvent: event,
                checkboxState: this.checked,
            });
        }
        else {
            if (!this.disabled && !this.readonly) {
                this.checked = event.target.checked;
                this.onModelChange(this.checked);
                this.onChange.emit({
                    originEvent: event,
                    checkboxState: this.checked,
                });
            }
        }
    }
    // Switch the checked state of the checkbox
    toggle(event) {
        if (this.disabled || this.readonly)
            return;
        if (this.indeterminate) {
            this.checked = true;
            this.indeterminate = false;
            this.onIndeterminateChange.emit(this.indeterminate);
            this.onModelChange(this.checked);
        }
        else {
            this.checked = !this.checked;
            this.onModelChange(this.checked);
        }
        this.onChange.emit({
            originEvent: event,
            checkboxState: this.checked,
        });
    }
    setCheckboxState(event) {
        if (this.disabled || this.readonly)
            return;
        this.checked = event.indeterminate ? false : event.checked;
        this.indeterminate = event.indeterminate || false;
        this.onIndeterminateChange.emit(this.indeterminate);
        this.onModelChange(this.checked);
        this.onChange.emit({
            originEvent: event.originEvent,
            checkboxState: this.checked,
        });
        this.cd.markForCheck();
    }
    onFocus(event) {
        this.focused = true;
    }
    onBlur(event) {
        this.focused = false;
        this.onModelTouched();
    }
    focus() {
        this.inputViewChild.nativeElement.focus();
    }
    onKeydown(event) {
        if (this.stopKeydownPropagation) {
            event.stopPropagation();
        }
        let keyCode = event.keyCode;
        switch (keyCode) {
            case 13:
            case 32:
                event.preventDefault();
                if (this.indeterminate) {
                    this.checked = true;
                    this.indeterminate = false;
                    this.onIndeterminateChange.emit(this.indeterminate);
                    this.onModelChange(this.checked);
                }
                else {
                    if (!this.disabled && !this.readonly) {
                        this.checked = !this.checked;
                        this.onModelChange(this.checked);
                        this.onChange.emit({
                            originEvent: event,
                            checkboxState: this.checked,
                        });
                    }
                }
                break;
        }
    }
    onClickLabel(event) {
        event.stopPropagation();
        event.preventDefault();
        if (this.indeterminate) {
            this.checked = true;
            this.indeterminate = false;
            this.onIndeterminateChange.emit(this.indeterminate);
            this.onModelChange(this.checked);
            this.onChange.emit({
                originEvent: event,
                checkboxState: this.checked,
            });
        }
        else {
            if (!this.disabled && !this.readonly) {
                this.checked = !this.checked;
                this.onModelChange(this.checked);
                this.onChange.emit({
                    originEvent: event,
                    checkboxState: this.checked,
                });
            }
        }
    }
    writeValue(model) {
        this.model = model;
        this.checked = this.isChecked();
        this.cd.markForCheck();
    }
    isChecked() {
        return this.model;
    }
    registerOnChange(fn) {
        this.onModelChange = fn;
    }
    registerOnTouched(fn) {
        this.onModelTouched = fn;
    }
    setDisabledState(val) {
        this.disabled = val;
        this.cd.markForCheck();
    }
    ngAfterViewInit() {
        const v = this.checkboxLabel.nativeElement.textContent.trim();
        if (v.length > 0 && !this.ariaLabel) {
            this.ariaLabel = v;
        }
    }
    ngOnDestroy() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CheckboxComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.FocusIndicatorService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.11", type: CheckboxComponent, selector: "ap-checkbox", inputs: { disabled: "disabled", readonly: "readonly", ariaLabel: "ariaLabel", tabindex: "tabindex", inputId: "inputId", required: "required", name: "name", ariaLabelledBy: "ariaLabelledBy", ariaDescribedby: "ariaDescribedby", indeterminate: "indeterminate", styleClass: "styleClass", style: "style", labelPosition: "labelPosition", stopKeydownPropagation: "stopKeydownPropagation" }, outputs: { onChange: "onChange", onIndeterminateChange: "onIndeterminateChange" }, providers: [CHECKBOX_VALUE_ACCESSOR], queries: [{ propertyName: "templates", predicate: CustomizeTemplate }], viewQueries: [{ propertyName: "inputViewChild", first: true, predicate: ["cb"], descendants: true }, { propertyName: "checkboxLabel", first: true, predicate: ["checkboxLabel"], descendants: true }], exportAs: ["apCheckbox"], usesOnChanges: true, ngImport: i0, template: `
        <div
            class="ap-checkbox-container"
            [class.ap-checkbox-container-reverse]="labelPosition === 'before'"
        >
            <div
                class="ap-checkbox {{ styleClass }}"
                [class.ap-checkbox-checked]="checked"
                [class.ap-checkbox-disabled]="disabled"
                [class.ap-checkbox-readonly]="readonly"
                [class.ap-checkbox-focus]="focused"
                [class.ap-checkbox-indeterminate]="indeterminate"
                (keydown)="onKeydown($event)"
            >
                <div class="a-hidden-accessibility">
                    <input
                        #cb
                        role="checkbox"
                        type="checkbox"
                        [attr.id]="inputId"
                        [attr.name]="name"
                        [attr.aria-checked]="indeterminate ? 'mixed' : checked"
                        [attr.aria-readonly]="readonly"
                        [attr.aria-disabled]="disabled"
                        [attr.aria-label]="ariaLabel"
                        [attr.aria-labelledby]="ariaLabelledBy"
                        [attr.aria-describedby]="ariaDescribedby"
                        [attr.tabindex]="tabindex"
                        class="ap-checkbox-input"
                        [checked]="checked"
                        [disabled]="disabled"
                        [readonly]="readonly"
                        [required]="required"
                        (focus)="onFocus($event)"
                        (blur)="onBlur($event)"
                        (click)="onCheckedChange($event)"
                        aria-live="assertive"
                    />
                </div>
                <span
                    [attr.aria-hidden]="true"
                    class="icon-checkbox-deselected-fill Appkit4-icon"
                    (click)="!disabled && onClickLabel($event)"
                ></span>
                <span
                    [attr.aria-hidden]="true"
                    class="icon-checkbox-backdrop-outline"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <span
                        *ngIf="checked"
                        class="icon-checkbox-selected-fill Appkit4-icon"
                    ></span>
                    <span
                        *ngIf="indeterminate"
                        class="icon-checkbox-indeterminate-fill Appkit4-icon"
                    ></span>
                </span>
            </div>
            <ng-container *ngIf="checked && templateMap">
                <label
                    #checkboxLabel
                    [attr.aria-hidden]="true"
                    [attr.for]="inputId"
                    class="ap-checkbox-label"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <ng-container
                        *ngTemplateOutlet="
                            templateMap && templateMap['checkedLabel']
                        "
                    ></ng-container>
                </label>
            </ng-container>
            <ng-container *ngIf="!checked && templateMap">
                <label
                    #checkboxLabel
                    [attr.aria-hidden]="true"
                    [attr.for]="inputId"
                    class="ap-checkbox-label"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <ng-container
                        *ngTemplateOutlet="templateMap['uncheckedLabel']"
                    ></ng-container>
                </label>
            </ng-container>
            <ng-container *ngIf="!templateMap">
                <label
                    #checkboxLabel
                    [attr.aria-hidden]="true"
                    [attr.for]="inputId"
                    class="ap-checkbox-label"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <ng-content></ng-content>
                </label>
            </ng-container>
        </div>
    `, isInline: true, styles: [".ap-checkbox-container{display:flex}.ap-checkbox-container.ap-checkbox-container-reverse{flex-direction:row-reverse}.ap-checkbox-container.ap-checkbox-container-reverse .ap-checkbox-label{padding-left:0;padding-right:var(--spacing-2)}.ap-checkbox{position:relative;display:block;box-sizing:border-box;cursor:pointer;margin:0;padding:0;width:1.5rem;height:1.5rem}.ap-checkbox .a-hidden-accessibility{border:0;clip:rect(0 0 0 0);height:1.5rem;overflow:hidden;padding:0;position:absolute;width:1.5rem}.ap-checkbox .a-hidden-accessibility .ap-checkbox-input{cursor:default;-webkit-appearance:checkbox;padding:initial;border:initial}.ap-checkbox .a-hidden-accessibility input{transform:scale(0)}.ap-checkbox .Appkit4-icon{font-weight:var(--font-weight-2)}.ap-checkbox .Appkit4-icon:before{font-weight:var(--font-weight-2)}.ap-checkbox .icon-checkbox-deselected-fill{color:var(--color-text-body)}.ap-checkbox .icon-checkbox-backdrop-outline{display:none;color:var(--color-text-body)}.ap-checkbox .icon-checkbox-backdrop-outline .icon-checkbox-selected-fill,.ap-checkbox .icon-checkbox-backdrop-outline .icon-checkbox-indeterminate-fill{position:absolute;top:0;left:0;color:var(--color-background-icon-primary);font-size:1rem}.ap-checkbox.ap-checkbox-checked .icon-checkbox-deselected-fill,.ap-checkbox.ap-checkbox-indeterminate .icon-checkbox-deselected-fill{display:none}.ap-checkbox.ap-checkbox-checked .icon-checkbox-backdrop-outline,.ap-checkbox.ap-checkbox-indeterminate .icon-checkbox-backdrop-outline{display:inline-flex}.ap-checkbox.ap-checkbox-disabled,.ap-checkbox.ap-checkbox-disabled .ap-checkbox-input{cursor:not-allowed}.ap-checkbox.ap-checkbox-disabled .icon-checkbox-deselected-fill{opacity:var(--opacity-5)}.ap-checkbox.ap-checkbox-readonly .ap-checkbox-input{cursor:not-allowed}.ap-checkbox.ap-checkbox-checked.ap-checkbox-disabled .icon-checkbox-backdrop-outline,.ap-checkbox.ap-checkbox-indeterminate.ap-checkbox-disabled .icon-checkbox-backdrop-outline{opacity:var(--opacity-5);background:var(--color-background-icon-primary)}.ap-checkbox+.ap-checkbox-label{padding-left:var(--spacing-2);padding-right:0;line-height:1.5rem;display:block;font-size:1rem;color:var(--color-text-body);letter-spacing:-.025rem;cursor:pointer}.ap-checkbox+.ap-checkbox-label:empty{display:none}.ap-checkbox+.ap-checkbox-label:hover{cursor:pointer}.ap-checkbox.ap-checkbox-disabled+.ap-checkbox-label{opacity:var(--opacity-5);cursor:not-allowed}.ap-checkbox.ap-checkbox-readonly+.ap-checkbox-label{pointer-events:none}[data-keyboard-focus=true] .ap-checkbox.ap-checkbox-focus{outline:auto .125rem Highlight;outline:auto .3125rem -webkit-focus-ring-color;outline-offset:var(--spacing-1)}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: "ap-checkbox", exportAs: "apCheckbox", template: `
        <div
            class="ap-checkbox-container"
            [class.ap-checkbox-container-reverse]="labelPosition === 'before'"
        >
            <div
                class="ap-checkbox {{ styleClass }}"
                [class.ap-checkbox-checked]="checked"
                [class.ap-checkbox-disabled]="disabled"
                [class.ap-checkbox-readonly]="readonly"
                [class.ap-checkbox-focus]="focused"
                [class.ap-checkbox-indeterminate]="indeterminate"
                (keydown)="onKeydown($event)"
            >
                <div class="a-hidden-accessibility">
                    <input
                        #cb
                        role="checkbox"
                        type="checkbox"
                        [attr.id]="inputId"
                        [attr.name]="name"
                        [attr.aria-checked]="indeterminate ? 'mixed' : checked"
                        [attr.aria-readonly]="readonly"
                        [attr.aria-disabled]="disabled"
                        [attr.aria-label]="ariaLabel"
                        [attr.aria-labelledby]="ariaLabelledBy"
                        [attr.aria-describedby]="ariaDescribedby"
                        [attr.tabindex]="tabindex"
                        class="ap-checkbox-input"
                        [checked]="checked"
                        [disabled]="disabled"
                        [readonly]="readonly"
                        [required]="required"
                        (focus)="onFocus($event)"
                        (blur)="onBlur($event)"
                        (click)="onCheckedChange($event)"
                        aria-live="assertive"
                    />
                </div>
                <span
                    [attr.aria-hidden]="true"
                    class="icon-checkbox-deselected-fill Appkit4-icon"
                    (click)="!disabled && onClickLabel($event)"
                ></span>
                <span
                    [attr.aria-hidden]="true"
                    class="icon-checkbox-backdrop-outline"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <span
                        *ngIf="checked"
                        class="icon-checkbox-selected-fill Appkit4-icon"
                    ></span>
                    <span
                        *ngIf="indeterminate"
                        class="icon-checkbox-indeterminate-fill Appkit4-icon"
                    ></span>
                </span>
            </div>
            <ng-container *ngIf="checked && templateMap">
                <label
                    #checkboxLabel
                    [attr.aria-hidden]="true"
                    [attr.for]="inputId"
                    class="ap-checkbox-label"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <ng-container
                        *ngTemplateOutlet="
                            templateMap && templateMap['checkedLabel']
                        "
                    ></ng-container>
                </label>
            </ng-container>
            <ng-container *ngIf="!checked && templateMap">
                <label
                    #checkboxLabel
                    [attr.aria-hidden]="true"
                    [attr.for]="inputId"
                    class="ap-checkbox-label"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <ng-container
                        *ngTemplateOutlet="templateMap['uncheckedLabel']"
                    ></ng-container>
                </label>
            </ng-container>
            <ng-container *ngIf="!templateMap">
                <label
                    #checkboxLabel
                    [attr.aria-hidden]="true"
                    [attr.for]="inputId"
                    class="ap-checkbox-label"
                    (click)="!disabled && onClickLabel($event)"
                >
                    <ng-content></ng-content>
                </label>
            </ng-container>
        </div>
    `, providers: [CHECKBOX_VALUE_ACCESSOR], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, styles: [".ap-checkbox-container{display:flex}.ap-checkbox-container.ap-checkbox-container-reverse{flex-direction:row-reverse}.ap-checkbox-container.ap-checkbox-container-reverse .ap-checkbox-label{padding-left:0;padding-right:var(--spacing-2)}.ap-checkbox{position:relative;display:block;box-sizing:border-box;cursor:pointer;margin:0;padding:0;width:1.5rem;height:1.5rem}.ap-checkbox .a-hidden-accessibility{border:0;clip:rect(0 0 0 0);height:1.5rem;overflow:hidden;padding:0;position:absolute;width:1.5rem}.ap-checkbox .a-hidden-accessibility .ap-checkbox-input{cursor:default;-webkit-appearance:checkbox;padding:initial;border:initial}.ap-checkbox .a-hidden-accessibility input{transform:scale(0)}.ap-checkbox .Appkit4-icon{font-weight:var(--font-weight-2)}.ap-checkbox .Appkit4-icon:before{font-weight:var(--font-weight-2)}.ap-checkbox .icon-checkbox-deselected-fill{color:var(--color-text-body)}.ap-checkbox .icon-checkbox-backdrop-outline{display:none;color:var(--color-text-body)}.ap-checkbox .icon-checkbox-backdrop-outline .icon-checkbox-selected-fill,.ap-checkbox .icon-checkbox-backdrop-outline .icon-checkbox-indeterminate-fill{position:absolute;top:0;left:0;color:var(--color-background-icon-primary);font-size:1rem}.ap-checkbox.ap-checkbox-checked .icon-checkbox-deselected-fill,.ap-checkbox.ap-checkbox-indeterminate .icon-checkbox-deselected-fill{display:none}.ap-checkbox.ap-checkbox-checked .icon-checkbox-backdrop-outline,.ap-checkbox.ap-checkbox-indeterminate .icon-checkbox-backdrop-outline{display:inline-flex}.ap-checkbox.ap-checkbox-disabled,.ap-checkbox.ap-checkbox-disabled .ap-checkbox-input{cursor:not-allowed}.ap-checkbox.ap-checkbox-disabled .icon-checkbox-deselected-fill{opacity:var(--opacity-5)}.ap-checkbox.ap-checkbox-readonly .ap-checkbox-input{cursor:not-allowed}.ap-checkbox.ap-checkbox-checked.ap-checkbox-disabled .icon-checkbox-backdrop-outline,.ap-checkbox.ap-checkbox-indeterminate.ap-checkbox-disabled .icon-checkbox-backdrop-outline{opacity:var(--opacity-5);background:var(--color-background-icon-primary)}.ap-checkbox+.ap-checkbox-label{padding-left:var(--spacing-2);padding-right:0;line-height:1.5rem;display:block;font-size:1rem;color:var(--color-text-body);letter-spacing:-.025rem;cursor:pointer}.ap-checkbox+.ap-checkbox-label:empty{display:none}.ap-checkbox+.ap-checkbox-label:hover{cursor:pointer}.ap-checkbox.ap-checkbox-disabled+.ap-checkbox-label{opacity:var(--opacity-5);cursor:not-allowed}.ap-checkbox.ap-checkbox-readonly+.ap-checkbox-label{pointer-events:none}[data-keyboard-focus=true] .ap-checkbox.ap-checkbox-focus{outline:auto .125rem Highlight;outline:auto .3125rem -webkit-focus-ring-color;outline-offset:var(--spacing-1)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.FocusIndicatorService }], propDecorators: { disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], inputId: [{
                type: Input
            }], required: [{
                type: Input
            }], name: [{
                type: Input
            }], ariaLabelledBy: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], indeterminate: [{
                type: Input
            }], styleClass: [{
                type: Input
            }], style: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], stopKeydownPropagation: [{
                type: Input
            }], inputViewChild: [{
                type: ViewChild,
                args: ["cb"]
            }], checkboxLabel: [{
                type: ViewChild,
                args: ["checkboxLabel"]
            }], onChange: [{
                type: Output
            }], onIndeterminateChange: [{
                type: Output
            }], templates: [{
                type: ContentChildren,
                args: [CustomizeTemplate]
            }] } });
class CheckboxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.11", ngImport: i0, type: CheckboxModule, declarations: [CheckboxComponent, CustomizeTemplate], imports: [CommonModule], exports: [CheckboxComponent, CustomizeTemplate] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CheckboxModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [CheckboxComponent, CustomizeTemplate],
                    declarations: [CheckboxComponent, CustomizeTemplate],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CHECKBOX_VALUE_ACCESSOR, CheckboxComponent, CheckboxModule, CustomizeTemplate };
